import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import DataTable from 'components/shared/DataTable';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import { PERMISSIONS } from 'models/Profile';
import {
  adminPortalSystemQueryKeys,
  AdminRetrievalField,
  AdminScanField,
  showAdminPortalSystem,
  updateAdminPortalSystem,
} from 'services/api/admin/portalSystems';
import { portalSystemQueryKeys } from 'services/api/portalSystems';
import { colors } from 'styles/theme/colors';

import { primaryIdLabel } from './PortalSystems';

// Admin → Portal Systems → Edit (OLIO-3074): the selection side of a portal's
// collected-field configuration. The universe of collectable fields is code, so the
// tables list every field and lock the non-negotiables; the form only carries what
// the admin can move.
type FormValues = {
  retrievalFields: string[];
  scanFields: string[];
  sweepTableFields: string[];
  primaryId: string;
  urlRegex: string;
  homeUrl: string;
};

type PrimaryIdOption = { value: string; label: string };

function compiles(pattern: string) {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

// The standing HUD's recognition pattern (OLIO-3107). The two rules are validated here
// only — the api stores the pattern as given (agreed with the team): it must compile,
// and it must START with a capture group, because capture group 1 is the pointer that
// keys the retrieval run. `(?:`/`(?=` and friends open non-capturing constructs, so a
// leading `(` alone isn't enough.
const validationSchema = yup.object({
  urlRegex: yup.string().when('primaryId', {
    is: 'url',
    then: (schema) =>
      schema
        .test('compiles', 'Must be a valid regular expression', (value) => !value || compiles(value))
        .test(
          'capture-group',
          'Must start with a capture group — the capture is the referral pointer',
          (value) => !value || /^\((?!\?)/.test(value)
        ),
  }),
});

function toggled(names: string[], name: string, on: boolean) {
  return on ? [...names, name] : names.filter((n) => n !== name);
}

export default function EditPortalSystem() {
  const { id } = useParams();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);

  const form = useAdminForm({
    id,
    show: showAdminPortalSystem,
    create: async () => {},
    update: async (updateId: string, payload: FormValues) => {
      const updated = await updateAdminPortalSystem(updateId, payload);
      queryClient.invalidateQueries({ queryKey: portalSystemQueryKeys.base });
      return updated;
    },
    include: [] as const,
    queryKey: adminPortalSystemQueryKeys,
    schema: validationSchema,
    toFormValues: (data): FormValues => ({
      retrievalFields: (data?.retrievalFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      scanFields: (data?.scanFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      sweepTableFields: (data?.scanFields ?? []).filter((f) => f.showOnSweepsTable).map((f) => f.name),
      primaryId: data?.primaryId ?? 'url',
      urlRegex: data?.urlRegex ?? '',
      homeUrl: data?.homeUrl ?? '',
    }),
    toPayload: (values) => ({ ...values, urlRegex: values.urlRegex.trim() || null }),
    onUpdateSuccess: { toast: 'Portal system successfully updated!', navigate: '/admin/portal-systems' },
    onError: { toast: 'Something went wrong. Please try again.' },
  });

  const system = form.data;
  const values = form.values;

  const isCollected = (field: AdminRetrievalField | AdminScanField, selected: string[]) =>
    field.required || selected.includes(field.name);

  // A scan field the admin turns off cannot stay a table column; the api rejects the
  // combination, so drop the column in the same change.
  const setScanField = (name: string, on: boolean) =>
    form.setValues({
      scanFields: toggled(values.scanFields, name, on),
      sweepTableFields: on ? values.sweepTableFields : values.sweepTableFields.filter((n) => n !== name),
    });

  const retrievalHelper = createColumnHelper<AdminRetrievalField>();
  const retrievalColumns = useMemo(
    () => [
      retrievalHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      retrievalHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      retrievalHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.retrievalFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => form.setValue('retrievalFields', toggled(values.retrievalFields, field.name, on))}
              dataCyLabel={`retrieval-field-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.retrievalFields, canEdit]
  );

  const scanHelper = createColumnHelper<AdminScanField>();
  const scanColumns = useMemo(
    () => [
      scanHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      scanHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      scanHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.scanFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => setScanField(field.name, on)}
              dataCyLabel={`scan-field-${field.name}`}
            />
          );
        },
      }),
      scanHelper.display({
        id: 'sweepTable',
        header: 'Sweeps Table Column',
        size: 160,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={values.sweepTableFields.includes(field.name)}
              disabled={!canEdit || !isCollected(field, values.scanFields)}
              onChange={(on) => form.setValue('sweepTableFields', toggled(values.sweepTableFields, field.name, on))}
              dataCyLabel={`sweep-table-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.scanFields, values.sweepTableFields, canEdit]
  );

  const primaryIdOptions: PrimaryIdOption[] = useMemo(
    () => (system ? system.primaryIdOptions.map((value) => ({ value, label: primaryIdLabel(system, value) })) : []),
    [system]
  );

  if (form.loading || !system) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title='Edit Portal System' subtitle={system.displayName} />

      <HelpText>Collected fields apply to every client using this portal.</HelpText>

      <SectionHeader>Collected on Retrieval</SectionHeader>

      <TableWrapper data-testid='retrieval-fields-table'>
        <DataTable columns={retrievalColumns} data={system.retrievalFields} />
      </TableWrapper>

      <SectionHeader>Collected on Scans</SectionHeader>

      <TableWrapper data-testid='scan-fields-table'>
        <DataTable columns={scanColumns} data={system.scanFields} />
      </TableWrapper>

      <SectionHeader>Portal Home</SectionHeader>

      {/* The portal's own front door (OLIO-3131): where a first sweep recording starts, and the
          login anchor a credential save is verified against when nothing is recorded yet. Blank
          means "no configured door" — those surfaces fall back to the newest recording. */}
      <InputGroup title='Home URL (https only; blank for none)'>
        <UrlInput
          value={values.homeUrl}
          onChange={(e) => form.setValue('homeUrl', e.target.value.trim())}
          placeholder='https://portal.example.com/login'
          disabled={!canEdit}
          data-testid='portal-system-home-url'
        />
      </InputGroup>

      <SectionHeader>Primary ID</SectionHeader>

      <InputGroup title='Identifies a retrieval on the portal'>
        <SelectWrapper>
          <Select
            inputId='primary-id'
            options={primaryIdOptions}
            value={primaryIdOptions.find((option) => option.value === values.primaryId) ?? null}
            getOptionValue={(option: PrimaryIdOption | null) => option!.value}
            getOptionLabel={(option: PrimaryIdOption | null) => option!.label}
            onChange={(option: PrimaryIdOption | null) => form.setValue('primaryId', option?.value ?? 'url')}
            isDisabled={!canEdit}
          />
        </SelectWrapper>
      </InputGroup>

      {values.primaryId === 'url' && (
        <InputGroup title='Referral URL pattern' error={getFieldError(form.errors, 'urlRegex')}>
          <Input
            value={values.urlRegex}
            onChange={(e) => form.setValue('urlRegex', e.target.value)}
            placeholder={String.raw`(portal\.example\.com/referrals/\d+)`}
            $hasError={Boolean(getFieldError(form.errors, 'urlRegex'))}
            disabled={!canEdit}
            data-testid='url-regex'
          />
          <RegexHelpText>
            Tested against the URL body — no scheme, www., or query string. Capture group 1 is the referral pointer.
          </RegexHelpText>
        </InputGroup>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        {canEdit && (
          <Button
            data-testid='portal-systems-save'
            onClick={form.submit}
            disabled={!form.dirty || form.submitting}
            loading={form.submitting}
          >
            Update Portal System
          </Button>
        )}
      </ButtonGroup>
    </AdminFormPage>
  );
}

function FieldName({ field }: { field: AdminRetrievalField | AdminScanField }) {
  return (
    <FieldNameCell>
      <BoldText>{field.label}</BoldText>
      {field.required && <RequiredTag>required</RequiredTag>}
    </FieldNameCell>
  );
}

const HelpText = styled.p`
  color: ${colors.black50};
  font-size: 14px;
  margin: 0 0 20px;
`;

const RegexHelpText = styled.p`
  color: ${colors.black50};
  font-size: 12px;
  margin: 6px 0 0;
`;

const TableWrapper = styled.div`
  margin-bottom: 16px;
`;

const FieldNameCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const BoldText = styled.span`
  font-weight: 500;
`;

const TypeTag = styled.span`
  background: ${colors.black10};
  border-radius: 4px;
  color: ${colors.black50};
  font-size: 11px;
  padding: 2px 6px;
`;

const RequiredTag = styled(TypeTag)`
  color: ${colors.black75};
`;

const SelectWrapper = styled.div`
  max-width: 320px;
`;

const UrlInput = styled(Input)`
  max-width: 420px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
